import { toast } from 'sonner';
import { ensureAntiforgeryToken } from '@/api/antiforgery/antiforgery';
import { loginRequest, msalInstance } from '@/authConfig';
import { env } from '@/env';

/**
 * fetch-patch HTTP layer — mirrors Fireball.Ui/src/authConfig.ts's inline
 * window.fetch monkey-patch, extracted into its own module because email-hub
 * splits MSAL config (task [2.1], authConfig.ts) from the fetch patch (this
 * file, task [2.2]) and defers MSAL initialize()/login to the app shell
 * (task [3.1]).
 *
 * The patch:
 *   - injects an MSAL bearer token (acquired SILENTLY at request time via the
 *     shared msalInstance, so it never goes stale),
 *   - prefixes relative URLs with VITE_API_URL + '/api' — ONLY when VITE_API_URL
 *     is set (real mode; mock mode never invokes this layer),
 *   - attaches the antiforgery header on mutating verbs (POST/PUT/PATCH/DELETE),
 *   - centralizes error toasts (sonner) by HTTP status code.
 *
 * Installed by the app shell (task [3.1]) AFTER msalInstance.initialize() +
 * the redirect-login gate resolve. Exposed as an idempotent function rather
 * than an import side-effect so ordering is explicit and the patch is unit
 * testable (install -> assert -> restore).
 */

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

let patched = false;

/**
 * Acquire the MSAL id token silently for the active account. Mirrors
 * Fireball.Ui's `account.idToken` usage, but resolves at request time so a
 * refreshed token is always used. Returns null (send unauthenticated) if no
 * account exists or the instance is not yet initialized.
 */
async function acquireBearerToken(): Promise<string | null> {
	const account =
		msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
	if (!account) return null;

	try {
		const result = await msalInstance.acquireTokenSilent({
			...loginRequest,
			account,
		});
		return result.idToken;
	} catch {
		// acquireTokenSilent throws before initialize() or when interaction is
		// required; fall back to the account's cached id token.
		return account.idToken ?? null;
	}
}

/**
 * Real-mode URL resolution: prefix a relative path with VITE_API_URL + '/api'.
 * When VITE_API_URL is unset the app is in mock mode and this layer is not
 * invoked by callers — pass the input through untouched as a defensive default.
 */
function resolveUrl(input: string): string | URL {
	if (!env.VITE_API_URL) return input;
	return new URL('/api' + input, env.VITE_API_URL);
}

function toastByStatus(status: number): void {
	switch (status) {
		case 400:
		case 403:
			toast.error('Request forbidden', {
				dismissible: true,
				description: 'Your session may have expired. Please re-login.',
			});
			break;
		case 404:
			toast.error('Object not found', {
				dismissible: true,
				description:
					'The object you are looking for is not found. This incident has been reported.',
			});
			break;
		case 500:
			toast.error('Internal server error', {
				dismissible: true,
				description:
					'An internal server error occurred. This incident has been reported.',
			});
			break;
		default:
			toast.error('Some error occurred', {
				dismissible: true,
				description: 'An error occurred. This incident has been reported.',
			});
			break;
	}
}

/**
 * Monkey-patch window.fetch. Idempotent — a second call is a no-op, so double
 * wiring (e.g. app shell + a test) cannot stack wrappers.
 */
export function installFetchPatch(): void {
	if (patched) return;
	patched = true;

	const originalFetch = window.fetch.bind(window);

	window.fetch = async function patchedFetch(
		input: RequestInfo | URL,
		init?: RequestInit,
	): Promise<Response> {
		const nextInit: RequestInit = { ...init };
		const headers = new Headers(nextInit.headers);
		if (!nextInit.credentials) nextInit.credentials = 'include';

		const token = await acquireBearerToken();
		if (token) headers.set('Authorization', `Bearer ${token}`);

		const method = (nextInit.method ?? 'GET').toUpperCase();
		if (MUTATING_METHODS.has(method)) {
			try {
				const antiforgery = await ensureAntiforgeryToken();
				headers.set(antiforgery.headerName, antiforgery.requestToken);
			} catch (error) {
				console.error('Failed to get antiforgery token:', error);
			}
		}

		nextInit.headers = headers;

		const target = typeof input === 'string' ? resolveUrl(input) : input;

		try {
			const response = await originalFetch(target, nextInit);
			if (response.ok) return response;
			throw response;
		} catch (error) {
			if (error instanceof Response) {
				toastByStatus(error.status);
			} else {
				toast.error('Network error', {
					dismissible: true,
					description:
						'A network error occurred. This incident has been reported.',
				});
				console.error('Network error', error);
			}
			throw error;
		}
	};
}
